import { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { helpFunc } from "../../helpers/helpFunction";
import { patientService } from "../../services/patient.services";
import { compareAsc, compareDesc, isAfter, isEqual, parseISO } from "date-fns";

export const CheckinLogic = (
  checkin = null,
  patient,
  careID,
  careIsLocked,
  toggle = false
) => {
  const [state, setState] = useState({
    submit: false,
    activateForm: true,
    dataSaved: false,
    edit: false,
    locked: false,
  });
  const [error, setError] = useState("");
  const [oldesCare, setOldesCare] = useState({});
  const [open, setOpen] = useState(false);

  // Autosave draft handling: debounce timer + a flag that stays false until the
  // user actually edits the form, so programmatic setValue calls (initial
  // prefill, risk_level auto-calc, reuse of a previous VK) never persist a draft
  const saveTimeoutRef = useRef(null);
  const userEditedRef = useRef(false);

  const history = useHistory();
  const { id, care_id } = useParams();
  const edit = history?.location?.state?.edit;

  // Local storage helper functions
  const getLocalStorageKey = () =>
    `checkin_patient_${patient.id}_care_${careID}`;

  const saveToLocalStorage = (data) => {
    try {
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(data));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const savedData = localStorage.getItem(getLocalStorageKey());
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.error("Error loading from localStorage:", error);
      return null;
    }
  };

  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  };

  //Decide what data I need on component mount
  useEffect(() => {
    //Patient have checkin data already -> populate form
    if (checkin !== null) {
      setFormValues(checkin);
    } else if (careIsLocked) {
      deactivateForm();
    }
  }, []);

  useEffect(() => {
    const findOldCares = patient?.cares.filter((item) => item.checkin !== null);

    if (findOldCares) {
      const care = findOldCares?.reduce(
        (latest, current) => {
          if (current.finished_at === null) return latest;
          if (latest.finished_at === null) return current;

          const latestDate = parseISO(latest.finished_at);
          const currentDate = parseISO(current.finished_at);
          return compareAsc(currentDate, latestDate) === 1 ? current : latest;
        },
        { finished_at: null }
      );

      if (care.finished_at !== null && checkin === null) {
        setOpen(true);
        setOldesCare(care);
      }
    }
  }, []);

  const onPopUpClose = () => setOpen(false);

  const onPopUpSubmit = (item) => {
    // Reuse of a previous entry control: ignore any autosaved (empty) draft
    // and map the selected record directly into the form
    clearLocalStorage();
    setFormValues(item, { isReuse: true });
    setOpen(false);
  };

  //deactivate form when care is locked
  const deactivateForm = () => {
    setState({ ...state, activateForm: false });
  };

  //FORM SECTION
  const methods = useForm({
    defaultValues: {
      polypharmacy: false,
      drugs: [{ label: "Vyhledejte...", value: "" }],
      polypharmacy_note: "",
      high_interaction_potential: false,
      high_interaction_potential_drugs: [{ label: "Vyhledejte...", value: "" }],
      high_interaction_potential_note: "",
      toggle_diagnoses: false,
      diagnoses: [
        {
          label: "Vyhledejte...",
          value: "",
          drugs: [{ label: "Vyhledejte...", value: "" }],
        },
      ],
      diagnoses_note: "",
      renal_insufficiency: false,
      renal_insufficiency_note: "",
      significant_biochemical_changes: false,
      significant_biochemical_changes_note: "",
      systemic_corticosteroids: false,
      systemic_corticosteroids_note: "",
      narrow_therapeutic_window: false,
      narrow_therapeutic_window_drugs: [{ label: "Vyhledejte...", value: "" }],
      narrow_therapeutic_window_note: "",
      hepatic_insufficiency: false,
      hepatic_insufficiency_note: "",
      intensive_care: false,
      intensive_care_note: "",
      pharmacist_intervention_required: false,
      consultation_requested: false,
      risk_level: "none",
    },
  });

  // Watch form changes and save to localStorage with debounce
  useEffect(() => {
    const subscription = methods.watch((value, info) => {
      // Start autosaving only after a real user edit; ignore programmatic
      // setValue calls (prefill, risk_level auto-calc, reuse of previous VK)
      if (!userEditedRef.current) {
        if (info?.type !== "change") return;
        userEditedRef.current = true;
      }

      // Debounce the save operation
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveToLocalStorage(value);
      }, 500);
    });

    return () => {
      subscription.unsubscribe();
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [methods.watch]);

  // Map a CheckInNested record to the complete react-hook-form shape. All the
  // async option parsing is awaited up front so the form can be populated in a
  // single atomic methods.reset() call (see setFormValues).
  //
  // deriveToggles: when reusing a previous entry control, a section's switch is
  // turned on whenever the section actually carries content (drug list / note),
  // not only when its stored boolean is true - so the pharmacist sees every
  // prefilled section as active.
  const buildFormValues = async (data, deriveToggles = false) => {
    const drugs = await helpFunc.pDataForSelect(data.drugs, "drugs");
    const highInteractionDrugs = await helpFunc.pDataForSelect(
      data.high_interaction_potential_drugs,
      "drugs"
    );
    const narrowWindowDrugs = await helpFunc.pDataForSelect(
      data.narrow_therapeutic_window_drugs,
      "drugs"
    );
    const diagnoses = await helpFunc.pDataForSelect(data.diagnoses, "diagnosis");

    const hasOptions = (arr) =>
      Array.isArray(arr) &&
      arr.some((o) => o && o.value !== "" && o.value != null);
    const hasText = (s) => typeof s === "string" && s.trim() !== "";
    const sectionOn = (flag, ...signals) =>
      !!flag || (deriveToggles && signals.some(Boolean));

    return {
      polypharmacy: sectionOn(
        data.polypharmacy,
        hasOptions(drugs),
        hasText(data.polypharmacy_note)
      ),
      drugs,
      polypharmacy_note: data.polypharmacy_note,
      high_interaction_potential: sectionOn(
        data.high_interaction_potential,
        hasOptions(highInteractionDrugs),
        hasText(data.high_interaction_potential_note)
      ),
      high_interaction_potential_drugs: highInteractionDrugs,
      high_interaction_potential_note: data.high_interaction_potential_note,
      toggle_diagnoses: sectionOn(
        data.toggle_diagnoses,
        hasOptions(diagnoses),
        hasText(data.diagnoses_note)
      ),
      diagnoses,
      diagnoses_note: data.diagnoses_note,
      renal_insufficiency: sectionOn(
        data.renal_insufficiency,
        hasText(data.renal_insufficiency_note)
      ),
      renal_insufficiency_note: data.renal_insufficiency_note,
      significant_biochemical_changes: sectionOn(
        data.significant_biochemical_changes,
        hasText(data.significant_biochemical_changes_note)
      ),
      significant_biochemical_changes_note:
        data.significant_biochemical_changes_note,
      systemic_corticosteroids: sectionOn(
        data.systemic_corticosteroids,
        hasText(data.systemic_corticosteroids_note)
      ),
      systemic_corticosteroids_note: data.systemic_corticosteroids_note,
      narrow_therapeutic_window: sectionOn(
        data.narrow_therapeutic_window,
        hasOptions(narrowWindowDrugs),
        hasText(data.narrow_therapeutic_window_note)
      ),
      narrow_therapeutic_window_drugs: narrowWindowDrugs,
      narrow_therapeutic_window_note: data.narrow_therapeutic_window_note,
      hepatic_insufficiency: sectionOn(
        data.hepatic_insufficiency,
        hasText(data.hepatic_insufficiency_note)
      ),
      hepatic_insufficiency_note: data.hepatic_insufficiency_note,
      intensive_care: sectionOn(
        data.intensive_care,
        hasText(data.intensive_care_note)
      ),
      intensive_care_note: data.intensive_care_note,
      pharmacist_intervention_required: data.pharmacist_intervention_required,
      consultation_requested: data.consultation_requested,
      risk_level: data.risk_level,
    };
  };

  const setFormValues = async (data, { isReuse = false } = {}) => {
    setState({ ...state, loading: true });

    // Check if there are saved data in localStorage first
    const savedData = isReuse ? null : loadFromLocalStorage();

    // Populate the whole form in ONE atomic reset. Doing it as ~30 sequential
    // setValue calls leaves the form partially filled across the `await`
    // boundaries, and the per-section clearFormValues() effects
    // (PatientDrugsInpLogic / DiagnosesLogic) race with that and wipe the
    // values ("flash then blank"). reset() applies everything at once and
    // re-syncs the field arrays.
    let formValues;
    if (savedData) {
      formValues = { ...methods.getValues() };
      Object.keys(savedData).forEach((key) => {
        if (savedData[key] !== undefined && savedData[key] !== null) {
          formValues[key] = savedData[key];
        }
      });
    } else {
      formValues = await buildFormValues(data, isReuse);
    }

    methods.reset(formValues, { keepDefaultValues: true });

    if (!careIsLocked) {
      setState({ ...state, loading: false, edit: true });
    } else {
      setState({ ...state, loading: false, activateForm: false, locked: true });
    }
  };

  const consultationRequestedWatch = useWatch({
    control: methods.control,
    name: "consultation_requested",
  });

  //Control what the risk checkbox should be set
  const highRisk = useWatch({
    control: methods.control,
    name: "pharmacist_intervention_required",
  });
  const medRisk = useWatch({
    control: methods.control,
    name: [
      "polypharmacy",
      "high_interaction_potential",
      "toggle_diagnoses",
      "renal_insufficiency",
      "significant_biochemical_changes",
      "systemic_corticosteroids",
      "narrow_therapeutic_window",
      "hepatic_insufficiency",
      "intensive_care",
      "consultation_requested",
    ],
  });

  // useEffect(() => {
  //   if (!toggle && !edit) {
  //     console.log("som tu");
  //     if (highRisk) {
  //       methods.setValue("risk_level", "3");
  //     } else if (!medRisk.every((v) => v === false)) {
  //       methods.setValue("risk_level", "2");
  //     } else {
  //       methods.setValue("risk_level", "1");
  //     }
  //   }
  // }, [highRisk, medRisk, consultationRequestedWatch]);

  useEffect(() => {
    if (highRisk) {
      methods.setValue("risk_level", "3");
    } else if (!medRisk.every((v) => v === false)) {
      methods.setValue("risk_level", "2");
    } else {
      methods.setValue("risk_level", "1");
    }
  }, [highRisk, medRisk, consultationRequestedWatch]);

  const editCheckin = () => {
    history.push(`/patient-entry-control/${id}/care/${care_id}`, {
      edit: true,
    });
  };

  //Patching or posting data from form
  const saveData = async (data) => {
    //parse data for API
    //extract diagnoses from form data
    let diagnose = data.diagnoses;
    //delete data from checkin posting data
    delete data.diagnoses;
    data = {
      ...data,
      care: careID,
      drugs: helpFunc.objToArrApi(data.drugs),
      high_interaction_potential_drugs: helpFunc.objToArrApi(
        data.high_interaction_potential_drugs
      ),
      diagnoses_drugs: helpFunc.objToArrApi(data.diagnoses_drugs),
      narrow_therapeutic_window_drugs: helpFunc.objToArrApi(
        data.narrow_therapeutic_window_drugs
      ),
      risk_level: data.consultation_requested ? "none" : data.risk_level,
    };

    //Edit existing data
    if (edit != undefined && edit) {
      try {
        setState({ ...state, submit: true, dataSaved: false });
        //updating data in db
        await patientService.patchCheckIn(data, checkin.id);
        //updating diagnoses
        await patientService.patchDiagnoses(
          diagnose,
          "checkin-diagnosis",
          checkin.diagnoses,
          checkin.id
        );
        // Clear localStorage after successful save
        clearLocalStorage();
      } catch (error) {
        console.error(error);
        setError({ error: true, message: "Data nebyla uložena." });
        setState({ ...state, submit: false, dataSaved: false, popUp: true });
        return;
      }
    }
    //Saving new data
    else {
      try {
        setState({ ...state, submit: true, dataSaved: false });
        //posting new data to db
        const res = await patientService.postCheckIn(data);
        //posting new diagnoses
        await patientService.postDiagnoses(
          diagnose,
          "checkin-diagnosis",
          res.data.id
        );
        // Clear localStorage after successful save
        clearLocalStorage();
      } catch (error) {
        console.error(error);
        setError({ error: true, message: "Data nebyla uložena." });
        setState({ ...state, submit: false, dataSaved: false, popUp: true });
        return;
      }
    }
    setState({ ...state, submit: false, dataSaved: true });
    if (data.risk_level != 1) {
      history.push(
        `/patient-profile/${id}/care/${care_id}/pharmacological-plan`
      );
    } else {
      history.goBack();
    }
  };

  const returnBtn = () => {
    if (!state.dataSaved) {
      setError({
        error: true,
        message: "Při opuštění stránky nebudou záznamy uloženy.",
      });
      setState({ ...state, popUp: true });
    } else {
      history.push("/");
    }
  };

  const clearErrors = () => {
    setError({ error: false });
  };

  return {
    state,
    error,
    methods,
    editCheckin,
    saveData,
    returnBtn,
    clearErrors,
    onPopUpClose,
    onPopUpSubmit,
    open,
    oldesCare,
  };
};
